import {
  IconArrowRight as ArrowRight,
  IconFileCheck as FileCheck,
  IconLock as Lock,
  IconMapPin as MapPin,
  IconShieldCheck as ShieldCheck,
} from "@tabler/icons-react";

const heroImageSizes = "(min-width: 1280px) 48rem, (min-width: 1024px) calc(65vw - 4rem), 100vw";
const heroAvifSrcSet = [
  "/hero_mockup_640.avif 640w",
  "/hero_mockup_960.avif 960w",
  "/hero_mockup_1280.avif 1280w",
  "/hero_mockup_1600.avif 1600w",
  "/hero_mockup_2048.avif 2048w",
].join(", ");
const heroWebpSrcSet = [
  "/hero_mockup_640.webp 640w",
  "/hero_mockup_960.webp 960w",
  "/hero_mockup_1280.webp 1280w",
  "/hero_mockup_1600.webp 1600w",
  "/hero_mockup_2048.webp 2048w",
].join(", ");

export function Hero({ t }) {
  return (
    <section id="top" className="relative overflow-hidden pt-32 pb-20 md:pt-40 md:pb-28">
      <div
        className="orb"
        style={{
          width: 520,
          height: 520,
          background: "hsl(var(--brand) / 0.45)",
          top: -120,
          right: -80,
        }}
      />
      <div
        className="orb"
        style={{
          width: 360,
          height: 360,
          background: "hsl(var(--brand) / 0.25)",
          bottom: -60,
          left: -60,
        }}
      />

      <div className="container-x relative">
        <div className="grid items-center gap-12 lg:relative lg:min-h-[38rem] lg:grid-cols-12 lg:items-start xl:min-h-[42rem]">
          <div className="relative z-10 lg:col-span-6 lg:pt-8 xl:col-span-7">
            <div className="reveal eyebrow mb-6">
              <span className="pulse-dot inline-block h-1.5 w-1.5 rounded-full bg-brand" />
              {t.hero.eyebrow}
            </div>

            <h1 className="reveal text-[clamp(1.75rem,4vw,3.5rem)] font-medium leading-[1.25]">
              <span className="block">{t.hero.titleA}</span>
              <span className="block text-brand">{t.hero.titleB}</span>
              <span className="block mt-2">{t.hero.titleC}</span>
              <span className="block text-brand">{t.hero.titleD}</span>
            </h1>

            <HeroValue value={t.hero.value} />

            <div className="reveal mt-10 flex flex-wrap items-center gap-3">
              <a href="#contact" className="btn-pill btn-primary" data-testid="hero-cta-demo">
                {t.hero.ctaPrimary}
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="https://app.innospeq.com"
                target="_blank"
                rel="noreferrer"
                className="btn-pill btn-ghost px-5 py-3 text-sm md:hidden"
                data-testid="hero-app-link"
              >
                {t.nav.app}
                <ArrowRight className="h-4 w-4" />
              </a>
              <span className="ml-2 hidden items-center gap-1.5 text-xs text-muted-foreground md:inline-flex">
                <ShieldCheck className="h-3.5 w-3.5" />
                {t.hero.badgeLive}
              </span>
            </div>
          </div>

          <div className="reveal pointer-events-none lg:absolute lg:left-[35%] lg:right-0 lg:top-28 lg:z-0 xl:left-[34%] xl:top-24">
            <div className="relative ml-auto w-[min(100%,40rem)] lg:w-full">
              <div className="absolute inset-x-10 bottom-3 h-24 rounded-full bg-brand/25 blur-3xl dark:bg-brand/40 lg:inset-x-24 lg:h-36" />
              <picture>
                <source type="image/avif" srcSet={heroAvifSrcSet} sizes={heroImageSizes} />
                <source type="image/webp" srcSet={heroWebpSrcSet} sizes={heroImageSizes} />
                <img
                  src="/hero_mockup.png"
                  alt=""
                  aria-hidden="true"
                  width="4701"
                  height="2801"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  className="relative w-full drop-shadow-[0_36px_80px_rgba(3,21,63,0.26)] dark:drop-shadow-[0_36px_80px_rgba(27,90,243,0.2)]"
                />
              </picture>
            </div>
          </div>
        </div>

        <HeroTrustBand panel={t.hero.trustPanel} />
      </div>
    </section>
  );
}

function HeroTrustBand({ panel }) {
  const icons = [Lock, MapPin, FileCheck, ShieldCheck];

  return (
    <div className="reveal mt-5 overflow-hidden rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface)/0.82)] shadow-2xl shadow-[hsl(var(--brand)/0.08)] backdrop-blur-xl dark:bg-[hsl(var(--brand-deep)/0.78)] dark:shadow-[hsl(var(--brand)/0.18)]">
      <div className="relative">
        <div className="absolute inset-0 opacity-70 [background-image:radial-gradient(circle_at_12%_16%,hsl(var(--brand)/0.16),transparent_30%),linear-gradient(135deg,hsl(var(--brand-soft)/0.72),transparent_52%)] dark:opacity-45 dark:[background-image:radial-gradient(circle_at_12%_16%,hsl(var(--brand)/0.42),transparent_30%),linear-gradient(135deg,hsl(var(--brand)/0.18),transparent_52%)]" />

        <div className="relative grid gap-6 p-5 md:p-6 lg:grid-cols-[17rem_1fr] lg:gap-8">
          <div className="flex items-start gap-3 lg:block">
            <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-brand text-white shadow-lg shadow-[hsl(var(--brand)/0.24)] lg:mb-5">
              <ShieldCheck className="h-6 w-6" strokeWidth={1.8} />
            </span>
            <div>
              <div className="font-sans text-[0.68rem] font-semibold uppercase tracking-[0.18em] text-brand md:text-xs">
                {panel.eyebrow}
              </div>
              <p className="mt-2 max-w-sm font-sans text-sm leading-relaxed text-muted-foreground dark:text-white/68">
                {panel.footer}
              </p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {panel.items.map((item, index) => {
              const Icon = icons[index] || ShieldCheck;

              return (
                <div
                  key={item.title}
                  className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--surface)/0.64)] p-4 dark:border-white/12 dark:bg-white/[0.04]"
                >
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-brand-soft text-brand dark:bg-brand/15">
                    <Icon className="h-5 w-5" strokeWidth={1.8} />
                  </span>
                  <div className="mt-4 font-sans text-sm font-bold leading-snug text-[hsl(var(--foreground))] dark:text-white">
                    {item.title}
                  </div>
                  <div className="mt-1.5 font-sans text-xs leading-snug text-muted-foreground dark:text-white/62">
                    {item.desc}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroValue({ value }) {
  const [first, ...rest] = value.split("\n");

  return (
    <>
      <p className="reveal mt-8 max-w-xl text-base md:text-lg font-bold text-brand leading-relaxed">
        {first}
      </p>
      {rest.length > 0 && (
        <p className="reveal mt-4 max-w-xl text-base md:text-lg text-muted-foreground leading-relaxed">
          {rest.join(" ")}
        </p>
      )}
    </>
  );
}
